import { useState } from 'react';
import { useSnackbar } from 'notistack';

import HttpClient from '../../shared/infrastructures/clients/http.client';
import { extractTranslatedApiErrorMessage } from '../../shared/utils/translate-api-error.util';
import { DeliveryCity } from '../types/delivery-city.type';
import { UpdateDeliveryCityRequestDTO } from '../types/dtos/update-delivery-city.dto';
import { ADMIN_DELIVERY_CITIES_API_PATHS } from '../constants/delivery-cities-api-paths.constants';

type UpdateDeliveryCityParams = {
  deliveryCityId: string;
  deliveryCityData: UpdateDeliveryCityRequestDTO;
};

type UseUpdateDeliveryCityResult = {
  updateDeliveryCity: (params: UpdateDeliveryCityParams) => Promise<DeliveryCity | null>;
  isSubmitting: boolean;
};

export function useUpdateDeliveryCity(): UseUpdateDeliveryCityResult {
  const { enqueueSnackbar } = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateDeliveryCity = async ({
    deliveryCityId,
    deliveryCityData,
  }: UpdateDeliveryCityParams): Promise<DeliveryCity | null> => {
    setIsSubmitting(true);

    try {
      const updatedDeliveryCity = await HttpClient.patch<DeliveryCity, UpdateDeliveryCityRequestDTO>(
        ADMIN_DELIVERY_CITIES_API_PATHS.UPDATE_DELIVERY_CITY(deliveryCityId),
        deliveryCityData
      );

      enqueueSnackbar('Ville de livraison mise à jour avec succès', { variant: 'success' });
      return updatedDeliveryCity;
    } catch (error) {
      enqueueSnackbar(
        extractTranslatedApiErrorMessage(
          error,
          'Une erreur est survenue lors de la mise à jour de la ville de livraison'
        ),
        { variant: 'error' }
      );
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  return { updateDeliveryCity, isSubmitting };
}
